
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CalendarDays, Clock, User, UserCheck, Pencil, Trash2, Eye, Target, ChevronDown } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

interface TaskCardProps {
  task: {
    id: string;
    title: string;
    description?: string;
    status: "todo" | "in_progress" | "on_hold" | "done";
    priority: "low" | "medium" | "high";
    due_date?: string;
    start_date?: string;
    assignee?: {
      id: string;
      full_name?: string;
      email: string;
    };
    leads?: {
      id: string;
      name: string;
    };
    created_by_guest?: string;
    guest_email?: string;
    updated_by_guest?: string;
  };
  onEdit?: () => void;
  onDelete?: () => void;
  onTaskClick?: (task: any) => void;
  onDeleteClick?: (task: any) => void;
  onTaskComplete?: (taskId: string) => Promise<void>;
  onStatusChange?: (taskId: string, newStatus: string) => Promise<void>;
  canEdit?: boolean;
  userRole?: string;
}

const priorityColors = {
  low: "bg-blue-100 text-blue-800",
  medium: "bg-yellow-100 text-yellow-800",
  high: "bg-red-100 text-red-800",
};

const statusColors = {
  todo: "bg-gray-100 text-gray-800",
  in_progress: "bg-blue-100 text-blue-800",
  on_hold: "bg-orange-100 text-orange-800",
  done: "bg-green-100 text-green-800",
};

const statusLabels = {
  todo: "Por Hacer",
  in_progress: "En Progreso",
  on_hold: "En Pausa",
  done: "Completado",
};

const priorityLabels = {
  low: "Baja",
  medium: "Media",
  high: "Alta",
};

export const TaskCard = ({ task, onEdit, onDelete, onTaskClick, onDeleteClick, onTaskComplete, onStatusChange, canEdit = true }: TaskCardProps) => {
  const handleDetailsClick = (e: React.MouseEvent) => {
    console.log("TaskCard: Details button clicked!", { task: task.title });
    e.preventDefault();
    e.stopPropagation();
    
    if (onTaskClick) {
      console.log("TaskCard: Calling onTaskClick with task:", task);
      onTaskClick(task);
    } else {
      console.log("TaskCard: No onTaskClick handler provided!");
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    console.log("TaskCard: Delete button clicked");
    e.stopPropagation();
    e.preventDefault();
    
    if (onDeleteClick) {
      onDeleteClick(task);
    } else if (onDelete) {
      onDelete();
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    console.log("TaskCard: Edit button clicked");
    e.stopPropagation();
    e.preventDefault();
    
    if (onEdit) {
      onEdit();
    }
  };

  const handleCompleteClick = (e: React.MouseEvent) => {
    console.log("TaskCard: Complete button clicked");
    e.stopPropagation();
    e.preventDefault();
    
    if (onTaskComplete) {
      onTaskComplete(task.id);
    }
  };

  const handleStatusChange = (newStatus: string) => {
    if (onStatusChange && newStatus !== task.status) {
      onStatusChange(task.id, newStatus);
    }
  };

  const availableStatuses = [
    { value: "todo", label: "Por Hacer" },
    { value: "in_progress", label: "En Progreso" },
    { value: "on_hold", label: "En Pausa" },
    { value: "done", label: "Completado" }
  ];

  return (
    <Card className="w-full hover:shadow-md transition-shadow border-2 hover:border-primary/20">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <h3 className="font-semibold text-sm leading-tight">{task.title}</h3>
          {canEdit && (
            <div className="flex gap-1">
              {onEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleEditClick}
                  className="h-8 w-8 p-0"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
              )}
              {(onDelete || onDeleteClick) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDeleteClick}
                  className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          )}
        </div>
        
        <div className="flex gap-2 flex-wrap">
          {onStatusChange ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-auto p-0 hover:bg-transparent">
                  <Badge className={`${statusColors[task.status]} cursor-pointer hover:opacity-80 flex items-center gap-1`}>
                    {statusLabels[task.status]}
                    <ChevronDown className="h-3 w-3" />
                  </Badge>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {availableStatuses.map((status) => (
                  <DropdownMenuItem
                    key={status.value}
                    onClick={() => handleStatusChange(status.value)}
                    className={task.status === status.value ? "bg-muted" : ""}
                  >
                    <Badge className={`${statusColors[status.value as keyof typeof statusColors]} mr-2`}>
                      {status.label}
                    </Badge>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Badge className={statusColors[task.status]}>
              {statusLabels[task.status]}
            </Badge>
          )}
          <Badge className={priorityColors[task.priority]}>
            {priorityLabels[task.priority]}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0 space-y-3">
        {task.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {task.description}
          </p>
        )}
        
        {(task.start_date || task.due_date) && (
          <div className="space-y-1">
            {task.start_date && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <CalendarDays className="h-3 w-3" />
                <span>Inicio: {format(new Date(task.start_date), "dd MMM yyyy", { locale: es })}</span>
              </div>
            )}
            {task.due_date && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span>Vence: {format(new Date(task.due_date), "dd MMM yyyy", { locale: es })}</span>
              </div>
            )}
          </div>
        )}
        
        {/* Show lead, assignee or creator info */}
        <div className="space-y-1">
          {task.leads && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Target className="h-3 w-3" />
              <span>Lead: {task.leads.name}</span>
            </div>
          )}

          {task.assignee && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <UserCheck className="h-3 w-3" />
              <span>Asignado: {task.assignee.full_name || task.assignee.email}</span>
            </div>
          )}
          
          {task.created_by_guest && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <User className="h-3 w-3" />
              <span>Creado por: {task.created_by_guest}</span>
              {task.guest_email && (
                <span className="text-xs">({task.guest_email})</span>
              )}
            </div>
          )}
          
          {task.updated_by_guest && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Pencil className="h-3 w-3" />
              <span>Editado por: {task.updated_by_guest}</span>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDetailsClick}
            className="flex-1 h-8 text-xs"
          >
            <Eye className="h-3 w-3 mr-1" />
            Ver Detalles
          </Button>
          {task.status !== 'done' && onTaskComplete && (
            <Button
              variant="default"
              size="sm"
              onClick={handleCompleteClick}
              className="flex-1 h-8 text-xs"
            >
              Completar
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
